import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Linkedin, Instagram } from "lucide-react";
import {
  Shield, Brain, Palette, FileCheck, Award, BadgeCheck,
  Eye, Target, Heart, Briefcase, Users, TrendingUp,
  ChevronRight, PenTool,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import mahirahImgAsset from "@/assets/mahirah.jpeg.asset.json";
const mahirahImg = mahirahImgAsset.url;
import { useSitePage } from "@/lib/cms";
import { getRecordArray, getString, getStringArray } from "@/lib/pageContent";
import { MAHIRAH_PERSON, MVIBE_ORG, buildBreadcrumb, buildWebPage } from "@/lib/seo/schemas";

const badgeIcons = [Shield, Brain, Palette, FileCheck, Award, BadgeCheck, PenTool];
const visionIcons = [Eye, Target, Heart];
const corporateIcons = [Briefcase, Users, TrendingUp];

const fallbackBadges = [
  "Founder – MViBE",
  "Co-founder – Bold Bridge",
  "Life Coach",
  "Executive Facilitator",
  "Soft Skills Trainer",
  "Art Therapist",
  "Graphologist",
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const About = () => {
  const { data: page } = useSitePage("about");
  const content = (page?.content ?? {}) as Record<string, unknown>;

  const founderName = getString(content.founderName, "Mahirah (Sana Amrin)");
  const founderBadges = getStringArray(content.founderBadges, fallbackBadges);
  const founderParagraphs = getStringArray(content.founderParagraphs, []);
  const founderQuote = getString(content.founderQuote, "Clarity Within. Success Beyond.");
  const visionCards = getRecordArray<Record<string, unknown>>(content.visionCards, []).map((card, index) => ({
    title: getString(card.title, ["Vision", "Mission", "Values"][index] ?? ""),
    text: getString(card.text),
  }));
  const certifications = getStringArray(content.certifications, []);
  const methodology = getStringArray(content.methodology, []);
  const corporateBackground = getStringArray(content.corporateBackground, []);

  const aboutSchema = [
    MAHIRAH_PERSON,
    MVIBE_ORG,
    buildWebPage({
      name: "About Mahirah — Founder of MVIBE",
      description: "Meet Mahirah, ICF-aligned Executive Coach and founder of MVIBE.",
      path: "/about",
      type: "AboutPage",
    }),
    buildBreadcrumb([
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ]),
  ];

  return (
    <div>
      <SEOHead
        title={page?.seo.title ?? "About Mahirah — ICF Certified Executive Coach, Dubai"}
        description={page?.seo.description ?? "Meet Mahirah, ICF-certified executive coach and MVIBE founder. 15+ yrs HR leadership, 300+ coaching hours across Dubai, UAE and international clients."}
        canonical={page?.seo.canonical}
        ogTitle={page?.seo.ogTitle}
        ogDescription={page?.seo.ogDescription}
        ogImage={page?.seo.ogImage}
        type={page?.seo.type ?? "website"}
        schema={aboutSchema}
      />


      <section className="bg-primary py-14 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">About Us</span>
          </nav>
          <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold text-primary-foreground md:text-5xl lg:text-6xl">
            {getString(content.heroTitle, "Our Story: The Journey to Clarity Within. Success Beyond.")}
          </motion.h1>
          <motion.p {...fadeUp(0.1)} className="mx-auto mt-3 max-w-2xl font-sans text-lg text-primary-foreground/80">
            {getString(content.heroDescription, "Meet the founder and the philosophy behind every transformation at MVIBE")}
          </motion.p>
        </div>
      </section>

      <section className="bg-background py-12 md:py-16">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex flex-col items-center gap-4">
            <img src={mahirahImg} alt="Mahirah (Sana Amrin) — Founder of MVIBE" className="h-64 w-64 rounded-2xl border-4 border-accent object-cover shadow-xl md:h-72 md:w-72" loading="lazy" />
            <div className="flex gap-3">
              <a href="https://www.linkedin.com/in/coach-and-trainer-m" target="_blank" rel="noopener noreferrer" className="rounded-full bg-accent p-2 text-accent-foreground transition-colors hover:bg-accent/20 hover:text-accent" aria-label="LinkedIn"><Linkedin className="h-5 w-5" /></a>
              <a href="https://www.instagram.com/coachwithm" target="_blank" rel="noopener noreferrer" className="rounded-full bg-accent p-2 text-accent-foreground transition-colors hover:bg-accent/20 hover:text-accent" aria-label="Instagram"><Instagram className="h-5 w-5" /></a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl font-heading font-bold text-foreground md:text-5xl">{founderName}</h2>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {founderBadges.map((badge) => {
                const isFounder = badge.toLowerCase().includes("founder");
                return (
                  <span key={badge} className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${isFounder ? "bg-primary text-primary-foreground font-bold" : "border border-accent/30 bg-accent/15 text-accent-foreground"}`}>{badge}</span>
                );
              })}
            </div>

            <div className="mt-4 space-y-3">
              {founderParagraphs.map((paragraph) => (
                <p key={paragraph} className="font-sans text-base leading-relaxed text-muted-foreground">{paragraph}</p>
              ))}
            </div>

            <div className="mt-4 border-l-4 border-accent py-1.5 pl-4">
              <p className="text-xl font-heading font-semibold italic text-foreground">"{founderQuote}"</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-muted py-12">
        <div className="container mx-auto px-4">
          <div className="grid gap-6 md:grid-cols-3">
            {visionCards.map((card, i) => {
              const Icon = visionIcons[i] ?? Eye;
              return (
                <motion.div key={card.title} {...fadeUp(i * 0.1)} className="rounded-xl bg-card p-6 text-center shadow-sm">
                  <Icon className="mx-auto h-9 w-9 text-accent" />
                  <h3 className="mt-3 text-2xl font-heading font-bold text-foreground">{card.title}</h3>
                  <p className="mt-2 font-sans text-base leading-relaxed text-muted-foreground">{card.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.certificationsTitle, "Certifications & Professional Training")}</motion.h2>
          <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-4">
            {certifications.map((label, i) => {
              const Icon = badgeIcons[i] ?? Shield;
              return (
                <motion.div key={label} {...fadeUp(i * 0.08)} className="flex flex-col items-center rounded-xl bg-card p-5 shadow-sm">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/15">
                    <Icon className="h-6 w-6 text-accent" />
                  </div>
                  <p className="mt-3 text-base font-semibold text-foreground">{label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-muted py-12">
        <div className="container mx-auto px-4">
          <motion.h2 {...fadeUp()} className="text-center text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.methodologyTitle, "Our Facilitation & Coaching Methodology")}</motion.h2>
          <div className="mt-8 grid gap-5 md:grid-cols-4">
            {methodology.map((item, i) => (
              <motion.div key={item} {...fadeUp(i * 0.1)} className="relative rounded-xl bg-card p-5 text-center shadow-sm">
                <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-accent text-lg font-bold text-accent-foreground">{i + 1}</div>
                <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-12">
        <div className="container mx-auto px-4">
          <motion.h2 {...fadeUp()} className="text-center text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.corporateBackgroundTitle, "Corporate Background & Credibility")}</motion.h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {corporateBackground.map((item, i) => {
              const Icon = corporateIcons[i] ?? Briefcase;
              return (
                <motion.div key={item} {...fadeUp(i * 0.1)} className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <p className="font-sans text-base leading-relaxed text-muted-foreground">{item}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-accent py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-accent-foreground md:text-5xl">{getString(content.ctaTitle, "Ready to experience the MVIBE difference?")}</motion.h2>
          <motion.div {...fadeUp(0.15)} className="mt-6">
            <DiscoveryCallDialog>
              <Button size="lg" className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {getString(content.ctaButtonLabel, "Book a Discovery Call")}
              </Button>
            </DiscoveryCallDialog>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
